"use client"

import * as React from "react"
import Image from "next/image"
import { toast } from "sonner"
import { Download, Image as ImageGlyph, Link2 } from "lucide-react"
import { toBlob, toPng } from "html-to-image"

import { cn } from "@/lib/utils"
import { ArenaMark } from "@/components/arena/v2/primitives"
import { CreatorAvatar } from "@/components/creator-copy/avatar"
import { FIELD } from "@/components/creator-copy/field-card"
import { MOCK_LEADERBOARD, MOCK_MONTHLY_RUN, locksInLabel, type CreatorHome, type CreatorPick } from "@/lib/creator-copy/mock"
import { useHome, useViewer, type Viewer } from "@/lib/creator-copy/first-time"
import { pickLabel } from "@/components/creator-copy/home/shared"
import { ArrowDownIcon, ArrowUpIcon } from "@/components/ui/less-more-toggle"
import { selectActiveDays, selectTaken, useRunStore } from "@/lib/creator-copy/run-store"
import { PicksPIcon, TelegramIcon, XTwitterIcon } from "@/components/ui/icons"

/* =============================================================================
   The creator's own share card.

   WHY IT IS NOT THE INVITE MODAL. The invite poster sells the welcome bonus
   to a stranger. What a creator wants to send is themselves: what they have
   made, how often they are right, how many people follow and copy them. Same
   modal geometry as the invite (one 3:4 poster, a copy pill, the channel
   row), so this is the product's share flow wearing the creator's content
   rather than a second way to share.

   THE POSTER IS THE FIELD. Ground, lamp, halftone and the mark in the
   material, exactly as the Entries and creator cards draw them; the only
   accent is mint on what moved and pink on the foot that carries the code.
   ============================================================================= */

const FONT: React.CSSProperties = { fontFamily: "var(--font-open-sauce), system-ui, sans-serif" }

export type SharePoster = "profile" | "leaderboard" | "run" | "pick"

const usd = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** The window the poster's earnings cover: the Total earnings card's default range, so the two agree. */
const EARNINGS_WINDOW_DAYS = 30

/** Payouts over the poster's window and the last seven days, summed the way the Total earnings card sums them. */
function totals(home: CreatorHome) {
  const rows = home.overview.earnings
  const amountOf = (row: (typeof rows)[number]) => Number(row.amountLabel.replace(/[^0-9.]/g, ""))
  const within = (days: number) => rows.filter((row) => row.daysAgo < days).reduce((sum, row) => sum + amountOf(row), 0)
  return { total: within(EARNINGS_WINDOW_DAYS), week: within(7) }
}

function today() {
  return new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

/* --- The poster ground ----------------------------------------------------- */

/** Where the mark sits in the ground: its centre in % of the ground, its
    width in % of the ground, and how loud it is. */
interface MarkPlacement {
  cx: number
  cy: number
  w: number
  alpha: number
}
/** One placement per poster, set by hand against each layout on 2026-09-20:
    the mark sits in the top-right air the poster leaves free. Fixed. */
const MARK: Record<SharePoster, MarkPlacement> = {
  profile: { cx: 86.1, cy: 16.3, w: 23.8, alpha: 0.024 },
  pick: { cx: 76.4, cy: 17.1, w: 23.8, alpha: 0.024 },
  leaderboard: { cx: 86.7, cy: 16.6, w: 23.8, alpha: 0.024 },
  run: { cx: 76.1, cy: 17.6, w: 23.8, alpha: 0.024 },
}

function PosterGround({
  children,
  mark,
  onMark,
}: {
  children: React.ReactNode
  mark: MarkPlacement
  /** QA only: set, the mark can be dragged and scaled with the wheel. */
  onMark?: (next: MarkPlacement) => void
}) {
  const ground = React.useRef<HTMLDivElement>(null)
  /** The drag in flight: the body moves the mark; a corner (sx, sy = ±1)
      scales it about the opposite corner, proportions fixed. */
  const drag = React.useRef<{ x: number; y: number; start: MarkPlacement; corner?: [number, number] } | null>(null)
  const startDrag = (corner?: [number, number]) => (event: React.PointerEvent) => {
    if (!onMark) return
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { x: event.clientX, y: event.clientY, start: mark, corner }
  }
  const onPointerMove = (event: React.PointerEvent) => {
    if (!onMark || !drag.current || !ground.current) return
    const box = ground.current.getBoundingClientRect()
    const { start, corner } = drag.current
    const dx = ((event.clientX - drag.current.x) / box.width) * 100
    const dy = ((event.clientY - drag.current.y) / box.height) * 100
    if (!corner) {
      onMark({ ...start, cx: +(start.cx + dx).toFixed(1), cy: +(start.cy + dy).toFixed(1) })
      return
    }
    const [sx, sy] = corner
    // Height of the mark in % of the ground, from its width in % of the ground.
    const hOf = (w: number) => (w * box.width * (31 / 40.7)) / box.height
    const anchorX = start.cx - (sx * start.w) / 2
    const anchorY = start.cy - (sy * hOf(start.w)) / 2
    const w = Math.max(10, +(start.w + sx * dx).toFixed(1))
    onMark({ ...start, w, cx: +(anchorX + (sx * w) / 2).toFixed(1), cy: +(anchorY + (sy * hOf(w)) / 2).toFixed(1) })
  }
  const onPointerUp = () => {
    drag.current = null
  }
  const onWheel = (event: React.WheelEvent) => {
    if (!onMark) return
    const dir = event.deltaY < 0 ? 1 : -1
    onMark({ ...mark, alpha: +Math.max(0, mark.alpha + dir * 0.004).toFixed(3) })
  }
  const CORNERS: [number, number][] = [
    [-1, -1],
    [1, -1],
    [-1, 1],
    [1, 1],
  ]
  return (
    <div ref={ground} className="relative isolate flex-1 overflow-hidden" style={{ background: FIELD.creator.background }}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: `${FIELD.creator.glow}, linear-gradient(118deg, hsl(var(--black-500)) 0%, transparent 42%)` }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.16,
          mixBlendMode: "overlay",
          maskImage: "linear-gradient(to left, black 0%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(to left, black 0%, transparent 80%)",
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(120% 150% at 6% 100%, ${FIELD.creator.bloom} 0%, transparent 52%)` }}
      />
      <div
        onPointerDown={startDrag()}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        className={cn(
          "absolute aspect-[40.7/31] -translate-x-1/2 -translate-y-1/2",
          onMark ? "z-[5] cursor-move touch-none ring-1 ring-dashed ring-white/40" : "pointer-events-none"
        )}
        style={{ left: `${mark.cx}%`, top: `${mark.cy}%`, width: `${mark.w}%` }}
      >
        <ArenaMark className="block h-full w-full" alphaBoost={mark.alpha} />
        {onMark &&
          CORNERS.map(([sx, sy]) => (
            <span
              key={`${sx}${sy}`}
              onPointerDown={startDrag([sx, sy])}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className={cn(
                "absolute size-3 -translate-x-1/2 -translate-y-1/2 bg-white ring-1 ring-black/60",
                sx * sy > 0 ? "cursor-nwse-resize" : "cursor-nesw-resize"
              )}
              style={{ left: sx < 0 ? "0%" : "100%", top: sy < 0 ? "0%" : "100%" }}
            />
          ))}
      </div>
      <div className={cn("relative z-[4] flex h-full flex-col p-5", onMark && "pointer-events-none")}>{children}</div>
    </div>
  )
}

/** The foot that carries the code. The one pink surface on the card. */
function PosterFoot({ code }: { code: string }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-primary px-5 py-3.5">
      {/* 2026-10-17: the referral offer and its terms line, as production's share card. */}
      <span className="flex min-w-0 flex-col gap-1.5">
      <span className="flex items-center gap-2">
        <PicksPIcon className="h-[15px] w-[21px] text-white" />
        {/* The wordmark file carries its own pink mark at the left; on the
            pink foot that mark vanishes, so the letters are shown alone
            next to the white mark. Letters start at x=50 of the 272 box. */}
        <span className="block overflow-hidden" style={{ height: 12, width: 86 }}>
          <Image
            src="/getpicks-logo.svg"
            alt="GetPicks"
            width={272}
            height={31}
            style={{ height: 12, width: "auto", maxWidth: "none", marginLeft: -19 }}
          />
        </span>
      </span>
        <span className="block truncate text-[10px] font-semibold leading-none text-white/75">New users · after ID check · Terms apply</span>
      </span>
      <span className="shrink-0 text-right">
        <span className="flex items-center justify-end gap-1 text-[11px] font-semibold leading-none text-white">
          Get
          <span className="rounded-[4px] bg-gold px-1 py-0.5 font-open-sauce font-black text-grey-950">$5</span>
          with my code
        </span>
        <span className="mt-1 block font-open-sauce text-[15px] font-black leading-none tracking-tight text-white">{code}</span>
      </span>
    </div>
  )
}

function PosterHead({ caption }: { caption: string }) {
  const viewer = useViewer()
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="flex min-w-0 items-center gap-2.5">
        <CreatorAvatar
          name={viewer.name}
          handle={viewer.handle}
          src={viewer.avatar}
          portrait={viewer.portrait}
          size="md"
          className="size-11 shrink-0 ring-2 ring-white/20"
        />
        <span className="min-w-0">
          <span className="block truncate font-open-sauce text-[17px] font-black leading-none tracking-[-0.03em] text-white">
            {viewer.name}
          </span>
          <span className="mt-1.5 block truncate text-[12px] leading-none text-white-700">@{viewer.handle}</span>
        </span>
      </span>
      <span className="shrink-0 text-right text-[11px] font-semibold leading-none text-white-700">{caption}</span>
    </div>
  )
}

/**
 * The profile card: what the account has made, over the three numbers that
 * say whether it is worth following. The money is the figure; the rest sit
 * under it in one even row, the way the head prints them.
 */
function ProfilePoster() {
  const viewer = useViewer()
  const home = useHome()
  const { total, week } = totals(home)
  const cells = [
    { value: viewer.followers, label: "Followers" },
    { value: viewer.copies, label: "Copies" },
  ]
  return (
    <>
      <PosterHead caption={today()} />
      <div className="flex flex-1 flex-col justify-center py-6 text-center">
        <span className="block text-[13px] font-semibold tracking-tight text-white-700">Total earnings · {EARNINGS_WINDOW_DAYS} days</span>
        <span className="mt-2.5 block font-open-sauce text-[54px] font-black leading-none tracking-[-0.045em] tabular-nums text-white">
          {usd(total)}
        </span>
        <span className="mt-3 block font-open-sauce text-[14px] font-semibold tabular-nums tracking-tight text-creator">
          +{usd(week)} <span className="text-white-700">past 7 days</span>
        </span>
      </div>
      <div className="flex items-stretch divide-x divide-white/15 border-t border-white/15 pt-4">
        {cells.map((cell) => (
          <span key={cell.label} className="flex-1 px-2 text-center">
            <span className="block font-open-sauce text-[22px] font-black leading-none tabular-nums tracking-[-0.03em] text-white">
              {cell.value}
            </span>
            <span className="mt-1.5 block text-[12px] leading-none text-white-700">{cell.label}</span>
          </span>
        ))}
      </div>
    </>
  )
}

/**
 * The board card: the reader's own standing first, because that is what they
 * are sending, then the three above them by name.
 */
function LeaderboardPoster() {
  const viewer = useViewer()
  const top = MOCK_LEADERBOARD.slice(0, 3)
  return (
    <>
      <PosterHead caption={today()} />
      <div className="mt-5 flex items-end justify-between gap-3 border-b border-white/15 pb-5">
        <span>
          <span className="block text-[13px] font-semibold tracking-tight text-white-700">My rank</span>
          <span className="mt-2 block font-open-sauce text-[46px] font-black leading-none tracking-[-0.045em] tabular-nums text-white">
            {viewer.rank === null ? "Unranked" : `#${viewer.rank}`}
          </span>
        </span>
        <span className="pb-1 text-right">
          <span className="block font-open-sauce text-[15px] font-black leading-none tabular-nums tracking-tight text-white">
            {viewer.rep.toLocaleString("en-US")} xp
          </span>
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-center gap-1">
        <span className="mb-1 block text-[12px] font-semibold tracking-tight text-white-700">Top of the board</span>
        {top.map((row) => (
          <span key={row.id} className="flex items-center gap-3 py-2">
            <span className="w-4 shrink-0 font-open-sauce text-[14px] font-black leading-none tabular-nums text-white-700">{row.rank}</span>
            <CreatorAvatar name={row.name} handle={row.handle} size="sm" className="size-8 shrink-0" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-open-sauce text-[14px] font-semibold leading-none tracking-tight text-white">{row.name}</span>
              <span className="mt-1 block truncate text-[11px] leading-none text-white-700">@{row.handle}</span>
            </span>
            <span className="shrink-0 font-open-sauce text-[14px] font-black leading-none tabular-nums tracking-tight text-white">
              {row.rep.toLocaleString("en-US")}
            </span>
          </span>
        ))}
      </div>
    </>
  )
}

/**
 * The pick card: one open pick, sent as the creator's call. The player is the
 * figure, the side and line under them in the row's own words, then who is
 * already on it and when it locks.
 */
function PickPoster({ pick }: { pick: CreatorPick }) {
  const match = pick.match
  const caption = match ? `${match.league} · ${match.team} ${match.isIndividualSport ? "vs" : "@"} ${match.opponent} · ${match.eventTime}` : today()
  const cells = [
    { value: pick.copies.toLocaleString("en-US"), label: pick.copies === 1 ? "Copy" : "Copies" },
    { value: match?.eventTime ?? locksInLabel(pick.locksInMinutes), label: match ? "Game starts" : "Locks in" },
  ]
  return (
    <>
      <PosterHead caption={caption} />
      <div className="flex flex-1 flex-col items-center justify-center py-5 text-center">
        <span className="flex size-[104px] items-center justify-center overflow-hidden rounded-full bg-white/10 ring-2 ring-white/20">
          {pick.imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element -- league headshot hosts are not allowlisted
            <img src={pick.imageUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="font-open-sauce text-[28px] font-black text-white/70">{pick.player.slice(0, 1)}</span>
          )}
        </span>
        <span className="mt-4 block font-open-sauce text-[26px] font-black leading-none tracking-[-0.03em] text-white">{pick.player}</span>
        <span className="mt-2.5 flex items-center gap-1.5 font-open-sauce text-[17px] font-semibold tracking-tight text-creator">
          {pick.side === "more" ? "Over" : "Under"}
          {pick.side === "more" ? <ArrowUpIcon className="size-4" /> : <ArrowDownIcon className="size-4 text-red-400" />}
          <span className="tabular-nums">{pick.line}</span> {pick.statLabel}
        </span>
      </div>
      <div className="flex items-stretch divide-x divide-white/15 border-t border-white/15 pt-4">
        {cells.map((cell) => (
          <span key={cell.label} className="flex-1 px-2 text-center">
            <span className="block font-open-sauce text-[22px] font-black leading-none tabular-nums tracking-[-0.03em] text-white">{cell.value}</span>
            <span className="mt-1.5 block text-[12px] leading-none text-white-700">{cell.label}</span>
          </span>
        ))}
      </div>
    </>
  )
}

/**
 * The run card: nights played as the figure, what they earned under it, and
 * the track itself with its reward days, drawn the way the panel draws it.
 */
function RunPoster() {
  const run = MOCK_MONTHLY_RUN
  const activeDays = useRunStore(selectActiveDays)
  const taken = useRunStore(selectTaken)
  const month = new Date().toLocaleDateString("en-US", { month: "long" })
  return (
    <>
      <PosterHead caption={`Monthly Run · ${month}`} />
      <div className="flex flex-1 flex-col justify-end pb-5">
        <span className="flex items-baseline gap-2.5">
          <span className="font-open-sauce text-[64px] font-black leading-none tracking-[-0.045em] tabular-nums text-white">
            {activeDays}
          </span>
          <span className="font-open-sauce text-[20px] font-semibold tracking-tight text-white/70">
            {activeDays === 1 ? "night played" : "nights played"}
          </span>
        </span>
        <span className="mt-2 block text-[13px] tabular-nums text-white-700">
          {taken.length} of {run.rewards.length} rewards collected · {run.targetDays} nights to the last one
        </span>
      </div>
      <div className="flex items-center justify-between border-t border-white/15 pt-4">
        {Array.from({ length: run.targetDays }, (_, index) => {
          const day = index + 1
          const rung = run.rewards.some((r) => r.days === day)
          const reached = day <= activeDays
          return (
            <span
              key={day}
              className={cn(
                "block rounded-[3px]",
                rung ? "size-[18px] rounded-[6px]" : "size-2",
                reached ? (rung ? "bg-primary" : "bg-primary/60") : rung ? "bg-white/10 ring-1 ring-inset ring-white/20" : "bg-white/10"
              )}
            />
          )
        })}
      </div>
    </>
  )
}

/* --- The modal ------------------------------------------------------------- */

/** One way out of the modal: the glyph in its circle, the word under it. */
function Control({ label, onPress, children }: { label: string; onPress: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onPress} className="flex flex-col items-center gap-2 text-white/85 transition-colors hover:text-white">
      <span className="flex size-12 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-colors hover:bg-white/10">
        {children}
      </span>
      <span className="text-[11px] font-semibold tracking-tight text-white-700">{label}</span>
    </button>
  )
}

/** The words that ride with the link on X and Telegram, from the same figures the poster prints. */
function shareText(poster: SharePoster, viewer: Viewer, pick?: CreatorPick) {
  if (poster === "pick") return pick ? `My pick on GetPicks: ${pickLabel(pick)}.` : "My pick on GetPicks."
  if (poster === "leaderboard") return viewer.rank === null ? "I'm on the GetPicks creator board." : `#${viewer.rank} on the GetPicks creator board.`
  if (poster === "run") return "My Monthly Run on GetPicks."
  return `My GetPicks card: ${viewer.copies} copies.`
}

export function CreatorShareModal({
  open,
  onClose,
  poster,
  pick,
  code,
}: {
  open: boolean
  onClose: () => void
  poster: SharePoster
  /** The pick on the card when the poster is "pick". */
  pick?: CreatorPick
  /** The referral code the foot prints and the link carries. Defaults to the viewer's own. */
  code?: string
}) {
  const viewer = useViewer()
  const home = useHome()
  const referralCode = code ?? home.affiliate.referralTag
  const [mounted, setMounted] = React.useState(false)
  const [entered, setEntered] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const [busy, setBusy] = React.useState<"save" | "copy" | null>(null)
  /** The poster itself, so the picture people send is the card they see. */
  const posterRef = React.useRef<HTMLDivElement>(null)
  const url = `https://${home.affiliate.referralHost}${referralCode}`

  React.useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    setEntered(false)
    const id = window.setTimeout(() => setMounted(false), 280)
    return () => window.clearTimeout(id)
  }, [open])

  React.useEffect(() => {
    if (!mounted || !open) return
    let second = 0
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setEntered(true))
    })
    return () => {
      cancelAnimationFrame(first)
      if (second) cancelAnimationFrame(second)
    }
  }, [mounted, open])

  React.useEffect(() => {
    if (!mounted) return
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKey)
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener("keydown", onKey)
    }
  }, [mounted, onClose])

  if (!mounted) return null

  const copyLink = () => {
    // No clipboard at all (an insecure origin, an old browser) is a failure too, not silence.
    if (!navigator.clipboard) {
      toast.error("Could not copy")
      return
    }
    navigator.clipboard.writeText(url).then(
      () => {
        setCopied(true)
        toast.success("Link copied")
        window.setTimeout(() => setCopied(false), 2000)
      },
      () => toast.error("Could not copy")
    )
  }

  const share = (channel: "x" | "telegram") => {
    const text = shareText(poster, viewer, pick)
    const links = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    }
    window.open(links[channel], "_blank", "noopener,noreferrer")
  }

  const saveImage = async () => {
    if (!posterRef.current || busy) return
    setBusy("save")
    try {
      const data = await toPng(posterRef.current, { pixelRatio: 2, cacheBust: true })
      const link = document.createElement("a")
      link.href = data
      link.download = `picks-${poster}-card.png`
      link.click()
      toast.success("Image saved")
    } catch {
      toast.error("Could not make the image")
    } finally {
      setBusy(null)
    }
  }

  const copyImage = async () => {
    if (!posterRef.current || busy) return
    setBusy("copy")
    try {
      const blob = await toBlob(posterRef.current, { pixelRatio: 2, cacheBust: true })
      if (!blob) throw new Error("no image")
      // Safari and Firefox refuse image writes; those readers get the file.
      if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
        await saveImage()
        return
      }
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      toast.success("Image copied")
    } catch {
      toast.error("Could not copy the image")
    } finally {
      setBusy(null)
    }
  }


  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={
        poster === "profile" ? "Share your profile" : poster === "run" ? "Share your run" : poster === "pick" ? "Share your pick" : "Share your ranking"
      }
      style={FONT}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn("absolute inset-0 bg-black/85 backdrop-blur-sm transition-opacity duration-300 ease-out", entered ? "opacity-100" : "opacity-0")}
      />
      <div
        className={cn(
          "relative flex w-full max-w-[380px] flex-col gap-4 transition-all ease-out",
          entered ? "translate-y-0 opacity-100 duration-[420ms]" : "translate-y-4 opacity-0 duration-200"
        )}
      >
        <div ref={posterRef} className="flex aspect-[3/4] w-full flex-col overflow-hidden rounded-[18px] ring-1 ring-white/15">
          <PosterGround mark={MARK[poster]}>
            {poster === "profile" ? (
              <ProfilePoster />
            ) : poster === "run" ? (
              <RunPoster />
            ) : poster === "pick" && pick ? (
              <PickPoster pick={pick} />
            ) : (
              <LeaderboardPoster />
            )}
          </PosterGround>
          <PosterFoot code={referralCode} />
        </div>

        {/* Five ways out, in the order they get used: the picture first,
            because a card is sent as a picture, then the link, then the two
            places it gets posted. */}
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between px-1">
            <Control label={busy === "save" ? "Saving" : "Save image"} onPress={saveImage}>
              <Download className="size-[19px]" />
            </Control>
            <Control label={busy === "copy" ? "Copying" : "Copy image"} onPress={copyImage}>
              <ImageGlyph className="size-[19px]" />
            </Control>
            <Control label={copied ? "Copied" : "Copy link"} onPress={copyLink}>
              <Link2 className="size-[19px]" />
            </Control>
            <Control label="X" onPress={() => share("x")}>
              <XTwitterIcon className="size-[17px]" />
            </Control>
            <Control label="Telegram" onPress={() => share("telegram")}>
              <TelegramIcon className="size-[19px]" />
            </Control>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-9 text-[11px] font-semibold tracking-tight text-white-700 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
